import {
  OFFICE_365_DEFAULT_REDIRECT,
  OFFICE_365_LOGIN_PATH,
  OFFICE_365_LOGIN_URL,
  OFFICE_365_MICROSOFT_LOGOUT_BASE,
  OFFICE_365_TOKEN_PATH,
} from '@my-backoffice/app/pages/login/federated/office365/constants/office365-auth.constants';
import { toOffice365Auth2RedirectUrl, normalizeOffice365RedirectPath } from '@my-backoffice/app/pages/login/federated/office365/functions/office365-redirect-url.functions';
import { IOffice365AuthToken } from '@my-backoffice/app/pages/login/federated/office365/interfaces/office365-auth-token.interface';
import { IOffice365LoginInfo } from '@my-backoffice/app/pages/login/federated/office365/interfaces/office365-login-info.interface';
import { IOffice365UserInfo } from '@my-backoffice/app/pages/login/federated/office365/interfaces/office365-user-info.interface';
import { Office365UserPermissionsService } from '@my-backoffice/app/pages/login/federated/office365/services/office365-user-permissions.service';
import { IUser } from '@my-backoffice/app/pages/user/shared/models/interfaces/user.interface';
import { IAuthenticationService } from '@my-backoffice/app/shared/auth/authentication.service.interface';
import { FEDERATED_AUTH_ROUTES } from '@my-backoffice/app/shared/auth/login-routes.constants';
import { SERVER_URL } from '@my-backoffice/app/shared/constants/app.constants';
import { hasUserDataInSession } from '@my-backoffice/app/shared/functions/has-user-data-in-session.function';
import { EStorageKeys } from '@my-backoffice/app/shared/models/enums/storage-keys.enum';
import { IHttpResponseBody } from '@my-backoffice/app/shared/models/interfaces/http-response-body.interface';
import { HttpService, SkipInterceptorService } from '@my-backoffice/app/shared/services/http-services/http.service';
import { StorageService } from '@my-backoffice/app/shared/services/storage-services/storage.service';
import { LogrocketInitService } from '@my-backoffice/app/shared/services/third-party-services/logrocket-init/logrocket-init.service';
import { WINDOW } from '@my-backoffice/app/shared/tokens/window.token';
import { inject, Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CookieService } from 'ngx-cookie';
import { Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, share } from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class Office365AuthenticationService implements IAuthenticationService {
  public readonly currentUserSubject$: Subject<IUser> = new Subject<IUser>();
  public readonly refreshTokenExpire$: Subject<number> = new Subject<number>();

  private readonly _injector = inject(Injector);

  private _refreshAccessTokenInFlight$: Observable<IHttpResponseBody<IOffice365AuthToken>> | undefined;

  public constructor(
    private readonly _storageService: StorageService,
    private readonly _cookieService: CookieService,
    private readonly _httpService: HttpService,
    private readonly _httpSkipService: SkipInterceptorService,
    private readonly _router: Router,
    private readonly _userPermissionsService: Office365UserPermissionsService,
    @Inject(WINDOW) private readonly _window: Window
  ) {}

  public updateUserData(data: Readonly<IUser>): void {
    this.currentUserSubject$.next(data);
  }

  public navigateToLogin(redirectUrl?: string): void {
    const redirectPath = redirectUrl ?? `${location.pathname}${location.search}`;
    const querySuffixIndex = redirectPath.indexOf('?');
    const pathOnly = querySuffixIndex === -1 ? redirectPath : redirectPath.slice(0, querySuffixIndex);
    const querySuffix = querySuffixIndex === -1 ? '' : redirectPath.slice(querySuffixIndex);
    const normalizedRedirect = `${normalizeOffice365RedirectPath(pathOnly) ?? pathOnly}${querySuffix}`;
    const search = `?redirect=${encodeURIComponent(normalizedRedirect)}`;

    location.assign(`${FEDERATED_AUTH_ROUTES.office365.login}${search}`);
  }

  /**
   * Redirect browser to auth2 Azure login (used by login-entry component).
   * @param {string | undefined} redirectUrl - Path to return after login.
   * @returns {void}
   */
  public redirectToAuth2Login(redirectUrl?: string): void {
    const redirect = toOffice365Auth2RedirectUrl({
      redirectUrl,
      origin: this._window.location.origin,
      fallbackPath: OFFICE_365_DEFAULT_REDIRECT,
    });
    const search = `?redirect=${encodeURIComponent(redirect)}`;

    if (this._window.location.href.toLowerCase().includes('localhost')) {
      location.assign(`${OFFICE_365_LOGIN_URL}${search}`);
    } else {
      location.assign(`${SERVER_URL}${OFFICE_365_LOGIN_PATH}${search}`);
    }
  }

  /**
   * @param {string} code - OAuth authorization code.
   * @returns {Observable<IHttpResponseBody<IOffice365LoginInfo>>} Token exchange response.
   */
  public getAccessToken$(code: string): Observable<IHttpResponseBody<IOffice365LoginInfo>> {
    const url = `${SERVER_URL}${OFFICE_365_TOKEN_PATH}?code=${code}`;

    return this._httpSkipService.httpClient.get<IHttpResponseBody<IOffice365LoginInfo>>(url, { observe: 'body' });
  }

  /**
   * Persist login response and load permissions.
   * @param {IHttpResponseBody<IOffice365LoginInfo>} response - Auth2 login payload.
   * @param {string | undefined} redirectUrl - Post-login redirect.
   * @returns {void}
   */
  public handleLoginResponse(response: IHttpResponseBody<IOffice365LoginInfo>, redirectUrl?: string): void {
    if (response?.content?.authToken) {
      this.saveTokenAndExpiration(response.content.authToken);

      if (response.content.authClientId) {
        this._cookieService.put(EStorageKeys.CLIENT_ID, response.content.authClientId);
      }

      if (response.content.userInfo) {
        const { userInfo } = response.content;
        const userId = this._resolveOffice365UserId(userInfo);

        this._injector.get(LogrocketInitService).identifyUserForLogRocket(userInfo as IUser);
        this._storageService.setItem(EStorageKeys.USER_DATA, JSON.stringify(userInfo));
        this._storageService.setItem(EStorageKeys.USER_ID, userId);
        this._cookieService.put(EStorageKeys.USER_DATA, JSON.stringify(userInfo));
        this._cookieService.put(EStorageKeys.USER_ID, userId);
        this.updateUserData(userInfo as IUser);
        this._userPermissionsService.saveUserPermissionsToStore(redirectUrl);
      } else {
        void this._router.navigate(['/unauthorized']);
      }

      return;
    }

    console.error('Office 365 login response missing auth token');
    void this._router.navigate(['/unauthorized']);
  }

  /**
   * @returns {Observable<IHttpResponseBody<IOffice365AuthToken>>} Refreshed token response.
   */
  public refreshAccessToken$(): Observable<IHttpResponseBody<IOffice365AuthToken>> {
    if (this._refreshAccessTokenInFlight$) {
      return this._refreshAccessTokenInFlight$;
    }

    const token = this._cookieService.get(EStorageKeys.REFRESH_TOKEN) || '';
    const url = `${SERVER_URL}${OFFICE_365_TOKEN_PATH}?refreshToken=${token}`;

    const refreshAccessToken$ = this._httpService.httpClient.put<IHttpResponseBody<IOffice365AuthToken>>(url, null, { observe: 'body' }).pipe(
      share(),
      finalize((): void => {
        this._refreshAccessTokenInFlight$ = undefined;
      })
    );

    this._refreshAccessTokenInFlight$ = refreshAccessToken$;

    return refreshAccessToken$;
  }

  public refreshAccessToken(): void {
    void this.refreshAccessToken$()
      .pipe(untilDestroyed(this))
      .subscribe((response: IHttpResponseBody<IOffice365AuthToken>): void => {
        if (response?.content) {
          this.saveTokenAndExpiration(response.content);
        }
      });
  }

  public clearAuthenticationData(): void {
    this._storageService.clearAuthenticationData();
    this._clearAuthCookies();
  }

  public logout(): void {
    const clientId = this._cookieService.get(EStorageKeys.CLIENT_ID) ?? '';

    this.deleteToken$().pipe(untilDestroyed(this)).subscribe();
    this.clearAuthenticationData();

    this._window.location.replace(`${OFFICE_365_MICROSOFT_LOGOUT_BASE}${clientId}`);
  }

  public isAuthenticated(): boolean {
    return this._cookieService.get(EStorageKeys.USER_DATA) !== undefined && this._cookieService.get(EStorageKeys.USER_DATA) !== '';
  }

  public isValidSession(): boolean {
    const userId = this._cookieService.get(EStorageKeys.USER_ID);
    const token = this._cookieService.get(EStorageKeys.AUTH_TOKEN);

    return !!userId && !!token && !this._isTokenExpired(EStorageKeys.AUTH_TOKEN_EXPIRE);
  }

  public getCurrentUser(): IUser {
    const userDataJson = this._cookieService.get(EStorageKeys.USER_DATA) || '{}';

    try {
      return JSON.parse(userDataJson) as IUser;
    } catch {
      return {} as IUser;
    }
  }

  /**
   * @description
   * Copies user id and profile from auth cookies into sessionStorage when the tab has no session copy yet.
   * @returns {Promise<void>} Promise resolved when restore completes or is skipped.
   */
  public restoreUserDataToSession(): Promise<void> {
    if (hasUserDataInSession(this._storageService)) {
      return Promise.resolve();
    }

    const userId = this._cookieService.get(EStorageKeys.USER_ID);
    const userDataJson = this._cookieService.get(EStorageKeys.USER_DATA);
    const token = this._cookieService.get(EStorageKeys.AUTH_TOKEN);
    const isTokenExpired = this._isTokenExpired(EStorageKeys.AUTH_TOKEN_EXPIRE);

    if (!userId || !userDataJson || !token || isTokenExpired) {
      return Promise.resolve();
    }

    let user: IUser;

    try {
      user = JSON.parse(userDataJson) as IUser;
    } catch {
      return Promise.resolve();
    }

    this._storageService.setItem(EStorageKeys.USER_ID, userId);
    this._storageService.setItem(EStorageKeys.USER_DATA, userDataJson);
    this.updateUserData(user);

    return Promise.resolve();
  }

  /**
   * @returns {Observable<void>} Delete token API call.
   */
  public deleteToken$(): Observable<void> {
    return this._httpService.httpClient.delete<void>(`${SERVER_URL}${OFFICE_365_TOKEN_PATH}`, { observe: 'body' }).pipe(
      map((response: void): void => response),
      catchError((): Observable<void> => of(undefined))
    );
  }

  /**
   * @param {IOffice365AuthToken} token - Access and refresh token payload.
   * @returns {void}
   */
  public saveTokenAndExpiration(token: IOffice365AuthToken): void {
    const { accessToken, accessTokenExpire, refreshToken, refreshTokenExpire } = token || {};

    if (accessToken) {
      const normalizedToken = decodeURIComponent(accessToken).trim();

      this._cookieService.put(EStorageKeys.AUTH_TOKEN, normalizedToken);
      this._cookieService.put(EStorageKeys.AUTH_TOKEN_EXPIRE, `${accessTokenExpire}`);
      this._storageService.setItem(EStorageKeys.AUTH_TOKEN, normalizedToken);
      this._storageService.setItem(EStorageKeys.AUTH_TOKEN_EXPIRE, `${accessTokenExpire}`);
    }

    if (refreshToken) {
      this._cookieService.put(EStorageKeys.REFRESH_TOKEN, refreshToken);
      this._cookieService.put(EStorageKeys.REFRESH_TOKEN_EXPIRE, `${refreshTokenExpire}`);
      this._storageService.setItem(EStorageKeys.REFRESH_TOKEN, refreshToken);
      this._storageService.setItem(EStorageKeys.REFRESH_TOKEN_EXPIRE, `${refreshTokenExpire}`);
      this.updateRefreshToken(refreshTokenExpire ?? 0);
    }
  }

  public updateRefreshToken(expire: number): void {
    this.refreshTokenExpire$.next(expire);
  }

  /**
   * @param {IOffice365UserInfo} userInfo - Auth2 login user payload.
   * @returns {string} Resolved user id for cookies and headers.
   */
  private _resolveOffice365UserId(userInfo: IOffice365UserInfo): string {
    return userInfo.userId ?? userInfo.id ?? '';
  }

  private _isTokenExpired(tokenKey: EStorageKeys): boolean {
    const tokenExpiredTime = this._cookieService.get(tokenKey) || '0';

    return Date.now() / 1000 >= +tokenExpiredTime;
  }

  private _clearAuthCookies(): void {
    const authCookieKeys: EStorageKeys[] = [
      EStorageKeys.AUTH_TOKEN,
      EStorageKeys.AUTH_TOKEN_EXPIRE,
      EStorageKeys.REFRESH_TOKEN,
      EStorageKeys.REFRESH_TOKEN_EXPIRE,
      EStorageKeys.CLIENT_ID,
      EStorageKeys.USER_DATA,
      EStorageKeys.USER_ID,
      EStorageKeys.USER_PERMISSIONS,
      EStorageKeys.SESSION_ID,
    ];

    authCookieKeys.forEach((key): void => {
      this._cookieService.remove(key);
    });
  }
}
